// Detailed view of a single customer booking with voucher actions.
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Download, Mail, Printer, ChevronLeft, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { track, Events } from '../../lib/analytics';

const BookingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { customer, loading: authLoading } = useCustomerAuth();
  const { formatPrice } = useCurrency();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  const reload = () => {
    setLoading(true);
    api.get(`/customer/bookings/${id}`)
      .then((r) => setBooking(r.data))
      .catch(() => setBooking(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!customer) { setLoading(false); return; }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, id]);

  if (!authLoading && !customer) {
    return <div className="p-10 text-center">Please sign in to view this booking.</div>;
  }
  if (loading) return <div className="p-10 text-center text-gray-500">Loading…</div>;
  if (!booking) return <div className="p-10 text-center text-red-600">Booking not found.</div>;

  const snap = booking.item_snapshot || {};
  const pc = booking.provider_confirmation || {};
  const extras = booking.extras || {};
  const cancellation = booking.cancellation || {};
  const isHotel = booking.service === 'hotels';
  const isFlight = booking.service === 'flights';
  const isCancelled = booking.status === 'cancelled';

  // Build the bearer URL for voucher download.
  const token = localStorage.getItem('elite_customer_token');
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';

  const downloadPdf = async () => {
    try {
      const resp = await api.get(`/customer/vouchers/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `voucher-${id}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      track(Events.VOUCHER_DOWNLOADED, { booking_id: id });
    } catch (e) {
      toast.error('Could not download voucher PDF.');
    }
  };

  const printVoucher = () => {
    // Open the server-rendered HTML voucher in a new tab (with auth) and trigger print.
    fetch(`${apiBase}/api/customer/vouchers/${id}/html`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.text())
      .then((html) => {
        const w = window.open('', '_blank');
        if (!w) { toast.error('Pop-up blocked.'); return; }
        w.document.write(html);
        w.document.close();
        setTimeout(() => { try { w.print(); } catch { /* ignore */ } }, 400);
      })
      .catch(() => toast.error('Could not open voucher.'));
  };

  const emailVoucher = async () => {
    try {
      const r = await api.post(`/customer/vouchers/${id}/resend`);
      toast.success(`Voucher emailed to ${r.data.sent_to}` + (r.data.delivery?.provider === 'mock' ? ' (mock provider)' : ''));
    } catch {
      toast.error('Could not send email. Please try again.');
    }
  };

  const doCancel = async () => {
    if (!window.confirm('Cancel this booking? This action cannot be undone.')) return;
    setCancelling(true);
    track(Events.CANCELLATION_REQUESTED, { booking_id: id });
    try {
      await api.post(`/customer/bookings/${id}/cancel`);
      toast.success('Booking cancelled. Refund (if any) will reach you in 5-10 working days.');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancellation failed. Please contact support.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-950 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link to="/account/bookings" className="inline-flex items-center text-sm text-gray-600 hover:text-red-600 mb-4">
          <ChevronLeft className="h-4 w-4" /> Back to bookings
        </Link>

        {/* Status hero */}
        <Card className="overflow-hidden">
          <div className={`px-5 py-4 ${isCancelled ? 'bg-gray-800' : 'bg-gradient-to-r from-emerald-600 to-emerald-700'} text-white flex items-center gap-3`}>
            {isCancelled ? <X className="h-6 w-6" /> : <CheckCircle2 className="h-6 w-6" />}
            <div className="flex-1">
              <h1 className="font-bold text-lg" data-testid="booking-status-title">
                {isCancelled ? 'Booking Cancelled' : 'Booking Confirmed'}
              </h1>
              <p className="text-sm text-white/85">Booking ID: {booking.id}</p>
            </div>
            <Badge className="bg-white text-gray-900 uppercase font-bold text-[11px]">{booking.status?.replace(/_/g, ' ')}</Badge>
          </div>

          <CardContent className="p-5 space-y-5">
            {/* Hotel/Flight info */}
            {isHotel && (
              <div>
                <h2 className="text-xs uppercase tracking-wider text-gray-500 mb-1">Hotel</h2>
                <p className="font-bold text-gray-900 dark:text-white text-lg">{snap.name}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{snap.address || snap.area} · {snap.city || snap.destination_name}</p>
                <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                  <div><p className="text-xs text-gray-500">Check-in</p><p className="font-semibold">{extras.check_in || '—'}</p></div>
                  <div><p className="text-xs text-gray-500">Check-out</p><p className="font-semibold">{extras.check_out || '—'}</p></div>
                  <div><p className="text-xs text-gray-500">Room</p><p className="font-semibold">{snap.room_type || '—'}</p></div>
                  <div><p className="text-xs text-gray-500">Board</p><p className="font-semibold">{snap.board_name || '—'}</p></div>
                </div>
              </div>
            )}
            {isFlight && (
              <div>
                <h2 className="text-xs uppercase tracking-wider text-gray-500 mb-1">Flight</h2>
                <p className="font-bold text-gray-900 dark:text-white text-lg">{snap.airline} {snap.flight_number}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{snap.from_code} → {snap.to_code}</p>
                <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                  <div><p className="text-xs text-gray-500">Departure</p><p className="font-semibold">{extras.departure_date} {snap.departure}</p></div>
                  <div><p className="text-xs text-gray-500">Arrival</p><p className="font-semibold">{snap.arrival}</p></div>
                  <div><p className="text-xs text-gray-500">PNR</p><p className="font-semibold">{pc.pnr || '—'}</p></div>
                  <div><p className="text-xs text-gray-500">Class</p><p className="font-semibold">{snap.cabin_class || '—'}</p></div>
                </div>
              </div>
            )}

            {/* Supplier ref */}
            <div className="border-t border-gray-100 dark:border-gray-800 pt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-500">Supplier Reference</p>
                <p className="font-semibold text-gray-900 dark:text-white">{pc.confirmation_code || pc.booking_id || pc.pnr || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Supplier</p>
                <p className="font-semibold text-gray-900 dark:text-white">{(pc.provider || snap.provider || 'Elite Travo').toUpperCase()}</p>
              </div>
            </div>

            {/* Passengers */}
            <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
              <h2 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Passengers</h2>
              <ul className="text-sm text-gray-900 dark:text-white space-y-1">
                {(booking.passengers || []).map((p, i) => (
                  <li key={i}>{p.title || ''} {p.first_name} {p.last_name}</li>
                ))}
              </ul>
            </div>

            {/* Cancellation policies */}
            {snap.cancellation_policies && snap.cancellation_policies.length > 0 && !isCancelled && (
              <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
                <h2 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Cancellation Policy</h2>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                  {snap.cancellation_policies.map((p, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span>Charge of {formatPrice(p.amount)} applies from {p.from || p.deadline || 'now'}.</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Rate comments */}
            {snap.rate_comments && (
              <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
                <h2 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Important Information</h2>
                <p className="text-sm text-gray-700 dark:text-gray-300">{String(snap.rate_comments)}</p>
              </div>
            )}

            {/* Promotions */}
            {snap.promotions && snap.promotions.length > 0 && (
              <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
                <h2 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Promotions Included</h2>
                <ul className="text-sm text-emerald-700 dark:text-emerald-400 space-y-1">
                  {snap.promotions.map((p, i) => <li key={i}>• {p.name || String(p)}</li>)}
                </ul>
              </div>
            )}

            {/* Payment */}
            <div className="border-t border-gray-100 dark:border-gray-800 pt-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Total Paid</p>
                <p className="text-2xl font-bold text-red-600">{formatPrice(booking.total_amount || 0)}</p>
              </div>
              {isCancelled && cancellation.refund_amount > 0 && (
                <div className="text-right">
                  <p className="text-xs text-gray-500">Refund</p>
                  <p className="font-bold text-emerald-600">{formatPrice(cancellation.refund_amount)}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        {!isCancelled && (booking.status === 'confirmed' || booking.status === 'payment_captured') && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
            <Button onClick={downloadPdf} variant="outline" data-testid="voucher-download" className="text-xs sm:text-sm">
              <Download className="h-4 w-4 mr-1.5" /> Download PDF
            </Button>
            <Button onClick={printVoucher} variant="outline" data-testid="voucher-print" className="text-xs sm:text-sm">
              <Printer className="h-4 w-4 mr-1.5" /> Print
            </Button>
            <Button onClick={emailVoucher} variant="outline" data-testid="voucher-email" className="text-xs sm:text-sm">
              <Mail className="h-4 w-4 mr-1.5" /> Email me
            </Button>
            <Button onClick={doCancel} disabled={cancelling} variant="destructive" data-testid="booking-cancel" className="text-xs sm:text-sm">
              <X className="h-4 w-4 mr-1.5" /> {cancelling ? 'Cancelling…' : 'Cancel'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingDetailPage;
